import React, { useState, useEffect, useContext, useCallback } from 'react';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

// COMPONENTS
import TableComponent from 'components/common/tableComponent';
import SearchBar from 'components/administration/searchBarComponent';
import FolderColorPicker from 'components/administration/FolderColorPicker';

// ICONS
import { PlusCircle, Edit3, Trash, Check, X } from 'lucide-react';

// SERVICES
import { getColors, getResources, upsertResource, deleteResource } from 'services/resourceService';

function ManagementResources() {
    const { addToast } = useContext(ToastContext);
    const [resources, setResources] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [confirmingDeleteResourceId, setConfirmingDeleteResourceId] = useState(null);
    const [availableColors, setAvailableColors] = useState([]);

    // États pour le formulaire d'ajout/modification
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentResource, setCurrentResource] = useState(null);
    const [newResourceTitle, setNewResourceTitle] = useState('');
    const [newResourceLink, setNewResourceLink] = useState('');
    const [newResourceColor, setNewResourceColor] = useState('#000000');
    const [newResourceColorId, setNewResourceColorId] = useState(null);

    const loadColors = useCallback(async () => {
        try {
            const response = await getColors();
            // Trier les couleurs pour mettre la jaune en premier
            const sortedColors = response.data.sort((a, b) => {
                if (a.BCO_NAME.toLowerCase().includes('jaune')) return -1;
                if (b.BCO_NAME.toLowerCase().includes('jaune')) return 1;
                return 0;
            });
            setAvailableColors(sortedColors || []);
            // Définir la couleur jaune comme couleur par défaut
            if (sortedColors && sortedColors.length > 0) {
                const yellowColor = sortedColors.find(color => color.BCO_NAME.toLowerCase().includes('jaune'));
                if (yellowColor) {
                    setNewResourceColor(yellowColor.BCO_HEX);
                    setNewResourceColorId(yellowColor.BCO_ID);
                }
            }
        } catch (err) {
            console.error('Erreur lors du chargement des couleurs:', err);
            addToast('Erreur', 'Impossible de charger les couleurs disponibles.', null, null, 'error');
        }
    }, [addToast]);

    useEffect(() => {
        loadColors();
    }, [loadColors]);

    const loadResources = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getResources();
            setResources(response.data);
            setError(null);
        } catch (err) {
            setError('Impossible de charger les ressources.');
            addToast('Erreur', 'Impossible de charger les ressources.', null, null, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [addToast]);

    useEffect(() => {
        loadResources();
    }, [loadResources]);

    const handleOpenModal = (resourceToEdit = null) => {
        if (resourceToEdit) {
            setCurrentResource(resourceToEdit);
            setNewResourceTitle(resourceToEdit.BTN_TITLE);
            setNewResourceLink(resourceToEdit.BTN_URL || '');
            setNewResourceColor(resourceToEdit.BCO_HEX || '#000000');
            setNewResourceColorId(resourceToEdit.BCO_ID);
        } else {
            setCurrentResource(null);
            setNewResourceTitle('');
            setNewResourceLink('');
            const yellowColor = availableColors.find(color => color.BCO_NAME.toLowerCase().includes('jaune'));
            setNewResourceColor(yellowColor ? yellowColor.BCO_HEX : '#000000');
            setNewResourceColorId(yellowColor ? yellowColor.BCO_ID : null);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentResource(null);
        setNewResourceTitle('');
        setNewResourceLink('');
        setNewResourceColor('#000000');
        setNewResourceColorId(null);
    };

    const handleSaveResource = async (e) => {
        e.preventDefault();
        if (!newResourceTitle || !newResourceLink) {
            addToast('Erreur', 'Veuillez remplir tous les champs obligatoires.', null, null, 'error');
            return;
        }

        const resourcePayload = {
            title: newResourceTitle,
            link: newResourceLink,
            colorId: newResourceColorId,
            tileId: 12
        };

        if (currentResource && currentResource.BTN_ID) {
            resourcePayload.id = currentResource.BTN_ID;
        }

        setIsLoading(true);
        try {
            await upsertResource(resourcePayload);
            if (currentResource) {
                addToast('Succès', 'Ressource mise à jour.', null, null, 'success');
            } else {
                addToast('Succès', 'Ressource ajoutée.', null, null, 'success');
            }
            await loadResources();
            handleCloseModal();
        } catch (err) {
            const errorMessage = currentResource ? 'Impossible de mettre à jour la ressource.' : 'Impossible d\'ajouter la ressource.';
            setError(errorMessage);
            addToast('Erreur', errorMessage, null, null, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteResource = async (resourceId) => {
        setConfirmingDeleteResourceId(resourceId);
    };

    const executeDeleteResource = async (resourceId) => {
        const originalResources = [...resources];
        setResources(prevResources => prevResources.filter(resource => resource.BTN_ID !== resourceId));
        setConfirmingDeleteResourceId(null);

        try {
            await deleteResource(resourceId);
            addToast('Succès', 'Ressource supprimée.', null, null, 'success');
        } catch (err) {
            setResources(originalResources);
            setError('Impossible de supprimer la ressource.');
            addToast('Erreur', 'Impossible de supprimer la ressource.', null, null, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelDeleteConfirmation = () => {
        setConfirmingDeleteResourceId(null);
    };

    const columns = [
        {
            key: 'BTN_TITLE',
            label: 'Titre',
            type: 'text',
        },
        {
            key: 'BTN_URL',
            label: 'Lien',
            type: 'link',
        },
        {
            key: 'BCO_HEX',
            label: 'Couleur',
            type: 'color',
        },
        {
            key: 'actions',
            label: 'Actions',
            type: 'action',
        },
    ];

    const filteredResources = resources.filter(resource => {
        const searchTermLower = searchTerm.toLowerCase();
        return (
            (resource.BTN_TITLE && resource.BTN_TITLE.toLowerCase().includes(searchTermLower)) ||
            (resource.BTN_URL && resource.BTN_URL.toLowerCase().includes(searchTermLower))
        );
    });

    const tableRows = filteredResources.map(resource => ({
        ...resource,
        actions: confirmingDeleteResourceId === resource.BTN_ID ? [
            {
                icon: Check,
                variant: 'success',
                onClick: () => executeDeleteResource(resource.BTN_ID),
                label: 'Confirmer la suppression'
            },
            {
                icon: X,
                variant: 'error',
                onClick: cancelDeleteConfirmation,
                label: 'Annuler la suppression'
            }
        ] : [
            {
                icon: Edit3,
                variant: 'info',
                onClick: () => handleOpenModal(resource),
                label: 'Modifier'
            },
            {
                icon: Trash,
                variant: 'error',
                onClick: () => handleDeleteResource(resource.BTN_ID),
                label: 'Supprimer'
            }
        ]
    }));

    return (
        <div className="h-full flex flex-col">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <SearchBar
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher une ressource..."
                />
                <button
                    className="w-full sm:w-auto flex items-center justify-center px-4 py-2 text-blanc rounded-lg shadow-md bg-orange hover:bg-framboise transition-colors ease-in-out duration-300"
                    onClick={() => handleOpenModal()}
                >
                    <PlusCircle className="mr-2" />
                    Nouvelle ressource
                </button>
            </div>

            <div className="flex-1 min-h-0">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full bg-blanc dark:bg-dark-secondary rounded-lg shadow-custom">
                        <p className="text-gray-500 dark:text-gray-400">Chargement des ressources...</p>
                    </div>
                ) : error ? (
                    <div className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900 p-3 rounded-md text-center bg-blanc dark:bg-dark-secondary rounded-lg shadow-custom">{error}</div>
                ) : (
                    <TableComponent
                        columns={columns}
                        rows={tableRows}
                        emptyText="Aucune ressource à afficher."
                    />
                )}
            </div>

            {/* Modal pour ajouter/modifier une ressource */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-blanc dark:bg-dark-secondary rounded-lg shadow-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-blanc"
                        >
                            <X size={24} />
                        </button>
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-blanc mb-6">
                            {currentResource ? 'Modifier la ressource' : 'Ajouter une nouvelle ressource'}
                        </h2>
                        <form autoComplete="off" onSubmit={handleSaveResource} className="space-y-4">
                            <div>
                                <label htmlFor="resourceTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Titre de la ressource <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="resourceTitle"
                                    value={newResourceTitle}
                                    onChange={(e) => setNewResourceTitle(e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-dark focus:outline-none focus:ring-orange focus:border-orange dark:focus:ring-orange dark:focus:border-orange"
                                    placeholder="Ex: Documentation API"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="resourceLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Lien <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    id="resourceLink"
                                    value={newResourceLink}
                                    onChange={(e) => setNewResourceLink(e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-dark focus:outline-none focus:ring-orange focus:border-orange dark:focus:ring-orange dark:focus:border-orange"
                                    placeholder="Ex: https://example.com/docs"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="resourceColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Couleur
                                </label>
                                <FolderColorPicker
                                    colors={availableColors}
                                    selectedColor={newResourceColor}
                                    onChange={(hex, colorId) => {
                                        setNewResourceColor(hex);
                                        setNewResourceColorId(colorId);
                                    }}
                                    className="mt-2"
                                />
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-blanc bg-orange rounded-md hover:bg-framboise focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange dark:focus:ring-offset-dark"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Enregistrement...' : (currentResource ? 'Mettre à jour' : 'Créer la ressource')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManagementResources;
